/**
 * AP-40 7.3 — **Dashboard des Schriftverkehrs**, die Startseite des Hauptmenüpunkts
 * (`/correspondence/dashboard`). Jede Kachel ist ein Filter der Belegliste — der Server zählt über
 * denselben Filter, mit dem die Liste lädt (MD-25).
 */
import { Link } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { geldText } from '@symphenia/shared-types'
import { DashboardKopf, DashboardListe, Kachel, KachelRaster, LeerHinweis, useDashboard } from '@/components/dashboard'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { documentsApi } from '../api/documents.api'

interface BelegZeile {
  id: string
  kind: string
  number: string | null
  partnerName: string
  dueDate: string | null
  sentAt: string | null
  openAmount?: number
}

export default function DocumentDashboardPage() {
  const { t } = useTranslation()
  const { bereich, setBereich, daten, fehler, kachel } = useDashboard('correspondence', documentsApi.dashboard)
  const entwuerfe = kachel('drafts')
  const angebote = kachel('openOffers')
  const umsatz = kachel('revenue')
  const betrag = (key: string) => {
    const b = kachel(key)?.amount
    return typeof b === 'number' ? `${geldText(b)} offen` : undefined
  }
  const nachArt = entwuerfe?.byKind as Record<string, number> | undefined
  const nichtsOffen = daten && Object.values(daten.tiles).every((k) => !k.count)

  return (
    <div className={pageWide}>
      <DashboardKopf
        symbol={Mail}
        titel="Schriftverkehr"
        untertitel="Was entworfen, draußen und offen ist."
        bereich={bereich}
        onBereich={setBereich}
        umschalter={daten?.scopeSwitch !== false}
        beschriftung={{ all: 'alle', mine: 'meine' }}
        stand={daten?.stand}
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <KachelRaster>
        <Kachel
          titel="Entwürfe"
          kachel={entwuerfe}
          hinweis={nachArt && Object.keys(nachArt).length ? Object.entries(nachArt).map(([k, n]) => `${n} ${t(`documents.kinds.${k}`)}`).join(' · ') : undefined}
        />
        <Kachel titel="Angebote offen" kachel={angebote} hinweis={angebote ? `läuft in 7 Tagen ab: ${angebote.expiringIn7 as number}` : undefined} />
        <Kachel titel="Offene Posten" kachel={kachel('openItems')} hinweis={betrag('openItems')} />
        <Kachel titel="Überfällig" ton="gefahr" kachel={kachel('overdue')} hinweis={betrag('overdue')} />
        <Kachel titel="Mahnung fällig" ton="warnung" kachel={kachel('dunningDue')} hinweis={kachel('dunningDue') ? `bei ${kachel('dunningDue')!.partners as number} Partnern` : undefined} />
        <Kachel titel="Lieferscheine nicht verrechnet" kachel={kachel('unbilledDeliveryNotes')} />
        {/* AP-39 F/G: die zwei Einkaufs-Kacheln (AP-40 10.1-7). */}
        <Kachel titel="Preisanfragen ohne Antwort" ton="warnung" kachel={kachel('unansweredInquiries')} hinweis={kachel('unansweredInquiries') ? 'Frist vorbei' : undefined} />
        <Kachel
          titel="Bestellungen ohne Auftragsbestätigung"
          kachel={kachel('unconfirmedOrders')}
          hinweis={kachel('unconfirmedOrders')?.deviating ? `bestätigt mit Abweichung: ${kachel('unconfirmedOrders')!.deviating as number}` : undefined}
        />
        <Kachel
          titel="Umsatz dieser Monat"
          kachel={umsatz}
          hinweis={umsatz ? `${geldText(umsatz.amount as number)} netto · Vormonat ${geldText(umsatz.previousMonth as number)}` : undefined}
        />
      </KachelRaster>

      {nichtsOffen && (
        <LeerHinweis
          verweise={[
            { zu: '/correspondence', text: 'Belege' },
            { zu: '/correspondence/dunning', text: 'Mahnwesen' },
          ]}
        />
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <DashboardListe<BelegZeile>
          titel="Überfällige Rechnungen"
          zeilen={daten?.lists?.overdueInvoices as BelegZeile[] | undefined}
          schluessel={(z) => z.id}
          alle={kachel('overdue')?.href}
          leer="Keine überfällige Rechnung."
          zeile={(z) => (
            <>
              <span className="w-20 shrink-0 text-xs font-semibold text-destructive">{z.dueDate ? fmtDate(z.dueDate) : ''}</span>
              <Link to={`/correspondence/${z.id}`} className="min-w-0 flex-1 truncate hover:underline">
                <span className="mr-2 font-mono text-xs text-muted-foreground">{z.number}</span>
                {z.partnerName}
              </Link>
              {z.openAmount != null && <span className="w-24 shrink-0 text-right text-xs tabular-nums">{geldText(z.openAmount)}</span>}
            </>
          )}
        />
        <DashboardListe<BelegZeile>
          titel="Zuletzt gesendet"
          zeilen={daten?.lists?.recentlySent as BelegZeile[] | undefined}
          schluessel={(z) => z.id}
          leer="Noch nichts gesendet."
          zeile={(z) => (
            <>
              <span className="w-20 shrink-0 text-xs text-muted-foreground">{z.sentAt ? fmtDate(z.sentAt) : ''}</span>
              <Link to={`/correspondence/${z.id}`} className="min-w-0 flex-1 truncate hover:underline">
                <span className="mr-2 text-xs text-muted-foreground">{t(`documents.kinds.${z.kind}`)}</span>
                <span className="mr-2 font-mono text-xs text-muted-foreground">{z.number}</span>
                {z.partnerName}
              </Link>
            </>
          )}
        />
      </div>
    </div>
  )
}
